import React from "react"
import { VStack } from "@chakra-ui/react"
import { Control } from "react-hook-form"
import ConstrainedColorPicker from "components/BadgeBuilder/ReactHookForm/ConstrainedColorPicker"
import EditorPane from "components/CanvasEditor/EditorPane"
import PropertyRow from "components/CanvasEditor/Inspector/PropertyRow"
import ToggleButtonGroup, { ToggleButtonGroupItem } from "components/ReactHookForm/ToggleButtonGroup"
import {
  SolidLineIcon,
  DashedLineIcon,
  DottedLineIcon,
} from "components/BadgeBuilder/Icons"
import NumberProperty from "components/CanvasEditor/Inspector/NumberProperty"
import ColorProperty from "components/CanvasEditor/Inspector/ColorProperty"
import { usePrinterSwatches } from "components/BadgeBuilder/hooks/usePrinterColorConstraints"
import { BadgeElementFormValues } from "components/BadgeBuilder/Panels/EditorPanel/BadgeSidePanelForm"

type Props = {
  control: Control<BadgeElementFormValues>
  shapeType: "line" | "rectangle" | "oval"
}

const BadgeShapeFields = ({ control, shapeType }: Props) => {
  const swatches = usePrinterSwatches()
  const getTitle = () => {
    switch (shapeType) {
      case "line":
        return "Line"
      case "rectangle":
        return "Rectangle"
      case "oval":
        return "Oval"
      default:
        return "Shape"
    }
  }

  // Create style toggle options with icons
  const styleOptions = [
    {
      button: (selected: boolean, onClick: () => void) => (
        <ToggleButtonGroupItem
          selected={selected}
          onClick={onClick}
          title="Solid"
          size="sm"
        >
          <SolidLineIcon />
        </ToggleButtonGroupItem>
      ),
      value: "solid",
    },
    {
      button: (selected: boolean, onClick: () => void) => (
        <ToggleButtonGroupItem
          selected={selected}
          onClick={onClick}
          title="Dashed"
          size="sm"
        >
          <DashedLineIcon />
        </ToggleButtonGroupItem>
      ),
      value: "dashed",
    },
    {
      button: (selected: boolean, onClick: () => void) => (
        <ToggleButtonGroupItem
          selected={selected}
          onClick={onClick}
          title="Dotted"
          size="sm"
        >
          <DottedLineIcon />
        </ToggleButtonGroupItem>
      ),
      value: "dotted",
    },
  ]

  return (
    <EditorPane title={getTitle()}>
      <VStack gap={2} alignItems="stretch" w="full">
        <PropertyRow label="Style">
          <ToggleButtonGroup
            name="style"
            control={control}
            columns={3}
            spacing={2}
          >
            {styleOptions}
          </ToggleButtonGroup>
        </PropertyRow>
        <PropertyRow label="Thickness">
          <NumberProperty
            name="thickness"
            control={control}
            min={0.1}
            step={0.1}
            precision={2}
            w="full"
            size="sm"
          />
        </PropertyRow>
        {shapeType === "line" ? (
          <PropertyRow label="Color">
            <ConstrainedColorPicker
              name="color"
              control={control}
              showHelpText={false}
              size="sm"
            />
          </PropertyRow>
        ) : (
          <>
            <ColorProperty
              control={control}
              name="strokeColor"
              label="Stroke Color"
              {...swatches}
            />
            <ColorProperty
              control={control}
              name="fillColor"
              label="Fill Color"
              {...swatches}
            />
          </>
        )}
      </VStack>
    </EditorPane>
  )
}

export default BadgeShapeFields
